/**
 * useAssignmentsData - Manages assignments data
 *
 * This hook manages the list of assignments and individual assignment details.
 * Used by AssignmentsDashboardContainer and AssignmentDetailContainer.
 *
 * Follows the URL-as-source-of-truth pattern established by useSubjectsData:
 * - Auto-fetches list when urlGradeCode or urlTermNumber changes
 * - Auto-fetches detail when urlAssignmentId changes
 * - Caches list by grade+term, detail by assignment ID
 * - Supports invalidateAssignmentsCache for forced refetch
 */

import { useState, useCallback, useEffect, useRef, useReducer } from "react";
import { assignmentsApi } from "@/api/assignmentsApi";
import {
  mapAssignmentList,
  mapAssignmentDetail
} from "@utils/assignmentsMapper";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type {
  Assignment,
  AssignmentDetail
} from "@ts/interfaces/assignments/assignmentsInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import { logger } from "@/utils/loggingUtils";

interface UseAssignmentsDataParams {
  urlGradeCode: number | undefined;
  urlTermNumber: number | undefined;
  urlAssignmentId: string | undefined;
  /**
   * When false the hook stays at initial IDLE state and makes no API calls.
   * Follows the React Query `enabled` pattern — the caller controls when
   * fetching is allowed (e.g. feature-flag + assessed-grade gate).
   * Defaults to true for backwards compatibility.
   */
  enabled?: boolean;
}

export interface AssignmentsDataState {
  // List data
  assignments: Assignment[];
  listStatus: REQUEST_STATUS;
  listError: ErrorInterface | null;

  // Detail data
  assignmentDetail: AssignmentDetail | null;
  detailForId: string | undefined;
  detailStatus: REQUEST_STATUS;
  detailError: ErrorInterface | null;
}

export interface AssignmentsDataActions {
  invalidateAssignmentsCache: () => void;
  invalidateDetailCache: () => void;
}

export type AssignmentsData = AssignmentsDataState & AssignmentsDataActions;

export function useAssignmentsData({
  urlGradeCode,
  urlTermNumber,
  urlAssignmentId,
  enabled = true
}: UseAssignmentsDataParams): AssignmentsData {
  // List state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [listStatus, setListStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [listError, setListError] = useState<ErrorInterface | null>(null);

  // Detail state
  const [assignmentDetail, setAssignmentDetail] =
    useState<AssignmentDetail | null>(null);
  const [detailStatus, setDetailStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [detailError, setDetailError] = useState<ErrorInterface | null>(null);

  // Cache tracking via refs (same pattern as useSubjectsData)
  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  const lastFetchedTermRef = useRef<number | undefined>(undefined);
  const lastFetchedDetailIdRef = useRef<string | undefined>(undefined);

  // Invalidation counters — incrementing forces the useEffect to re-run
  const [listInvalidationCount, bumpListInvalidation] = useReducer(
    (c: number) => c + 1,
    0
  );
  const [detailInvalidationCount, bumpDetailInvalidation] = useReducer(
    (c: number) => c + 1,
    0
  );

  // Invalidate list cache so next render cycle will refetch
  const invalidateAssignmentsCache = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    lastFetchedTermRef.current = undefined;
    bumpListInvalidation();
  }, []);

  // Invalidate detail cache so next render cycle will refetch
  const invalidateDetailCache = useCallback(() => {
    lastFetchedDetailIdRef.current = undefined;
    bumpDetailInvalidation();
  }, []);

  // ── Auto-fetch list when grade or term changes ──────────────
  useEffect(() => {
    if (!enabled || !urlGradeCode) {
      setAssignments([]);
      setListStatus(REQUEST_STATUS.IDLE);
      setListError(null);
      lastFetchedGradeRef.current = undefined;
      lastFetchedTermRef.current = undefined;
      return;
    }

    // Already fetched for this grade+term during the current cache window.
    if (
      lastFetchedGradeRef.current === urlGradeCode &&
      lastFetchedTermRef.current === urlTermNumber
    ) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    lastFetchedTermRef.current = urlTermNumber;
    setListStatus(REQUEST_STATUS.PENDING);
    setListError(null);

    assignmentsApi
      .getAssignmentsList(urlGradeCode, urlTermNumber)
      .then(response => {
        // Guard against stale responses when params changed mid-flight
        if (
          lastFetchedGradeRef.current !== urlGradeCode ||
          lastFetchedTermRef.current !== urlTermNumber
        ) {
          return;
        }
        setAssignments(mapAssignmentList(response.data ?? []));
        setListStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when params changed mid-flight
        if (
          lastFetchedGradeRef.current !== urlGradeCode ||
          lastFetchedTermRef.current !== urlTermNumber
        ) {
          return;
        }
        logger.warn("Error fetching assignments list:", error);
        setListStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;
        lastFetchedTermRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setListError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch assignments"
        });
      });
  }, [enabled, urlGradeCode, urlTermNumber, listInvalidationCount]);

  // ── Auto-fetch detail when assignment ID changes ────────────
  // Guard: only fetch when the URL param looks like a real UUID.
  // React Router may match "term-1" as :assignmentId when the
  // assignments/term/:termId route could collide with assignments/:assignmentId.
  const isValidAssignmentId =
    !!urlAssignmentId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      urlAssignmentId
    );

  useEffect(() => {
    if (!enabled || !isValidAssignmentId || !urlGradeCode || !urlTermNumber) {
      setAssignmentDetail(null);
      setDetailStatus(REQUEST_STATUS.IDLE);
      setDetailError(null);
      lastFetchedDetailIdRef.current = undefined;
      return;
    }

    // Already fetched for this assignment ID.
    if (lastFetchedDetailIdRef.current === urlAssignmentId) {
      return;
    }

    lastFetchedDetailIdRef.current = urlAssignmentId;
    setDetailStatus(REQUEST_STATUS.PENDING);
    setDetailError(null);

    assignmentsApi
      .getAssignmentDetail(urlAssignmentId, urlGradeCode, urlTermNumber)
      .then(response => {
        // Guard against stale responses
        if (lastFetchedDetailIdRef.current !== urlAssignmentId) return;
        setAssignmentDetail(mapAssignmentDetail(response));
        setDetailStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses
        if (lastFetchedDetailIdRef.current !== urlAssignmentId) return;
        logger.warn("Error fetching assignment detail:", error);
        setDetailStatus(REQUEST_STATUS.ERROR);
        lastFetchedDetailIdRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setDetailError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch assignment detail"
        });
      });
  }, [enabled, urlAssignmentId, urlGradeCode, detailInvalidationCount]);

  return {
    // List state
    assignments,
    listStatus,
    listError,

    // Detail state
    assignmentDetail,
    detailForId: lastFetchedDetailIdRef.current,
    detailStatus,
    detailError,

    // Actions
    invalidateAssignmentsCache,
    invalidateDetailCache
  };
}
